// SigninHelper.tsx
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IError } from "../../interfaces/user/errors";
import { toast } from "react-toastify";
import { AppDispatch } from "../../store";
import { ISigninFormProps } from "../../interfaces/account/signup";
import { signin } from "../../data-access/apis/authentication/auth";
import { resetAuth } from "../../stores/auth-slice";

const signinFormProps: ISigninFormProps = {
  email: '',
  password: '',
  phone: '',    // Phone ko initialize karna
  username: '', // Username ko initialize karna
};

export const SigninHelper = () => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const account = useSelector((state: any) => state.auth);
  const dispatch: AppDispatch = useDispatch();
  const [formErrors, setFormErrors] = useState<IError[]>([]);
  const [values, setValues] = useState<ISigninFormProps>(signinFormProps);
  const navigate = useNavigate();

  const isFieldEmpty = (field: string) => {
    return field.trim() === '';
  };

  const validateInput = (input: string | undefined): IError | null => {
    if (!input || isFieldEmpty(input)) {
      return { field: 'input', message: 'Please enter your username, email, or phone.' };
    }
    return null;
  };

  const handleSignIn = async (e: React.FormEvent | any) => {
    e.preventDefault();
    const errors: IError[] = [];

    const inputError = validateInput(values.email || values.phone || values.username);
    if (inputError) {
      errors.push(inputError);
    }

    if (isFieldEmpty(values.password)) {
      errors.push({ field: 'password', message: 'Please enter your password.' });
    }

    setFormErrors(errors);

    if (errors.length === 0) {
      setIsProcessing(true);
    } else {
      errors.forEach((error) => {
        toast.error(`${error.message}`, {
          position: 'bottom-right',
          autoClose: 3000,
        });
      });
      return;
    }

    // Accept email, username, full name, or phone in the same input.
    const identifier = (values.email || '').trim();
    let transformedPayload: ISigninFormProps;

    if (/^\\d{10}$/.test(identifier)) {
      transformedPayload = { phone: identifier, password: values.password };
    } else if (/^\\S+@\\S+\\.\\S+$/.test(identifier)) {
      transformedPayload = { email: identifier, password: values.password };
    } else if (/^[a-zA-Z0-9_]+$/.test(identifier)) {
      transformedPayload = { username: identifier, password: values.password };
    } else {
      transformedPayload = { name: identifier, password: values.password };
    }

    try {
      await dispatch(signin(transformedPayload)); // Yahan par dispatch karenge sahi payload
    } catch (error: any) {
      toast.error('Sign in failed. Please try again.');
    } finally {
      setIsProcessing(false);
      setValues(signinFormProps); // Form fields ko reset karte hain
    }
  };

  useEffect(() => {
    if (account.statusCode === 200) {
      setIsProcessing(false);
      setValues(signinFormProps); // Form fields ko reset karte hain
      navigate('/');
      dispatch(resetAuth());
    }
  }, [account.statusCode, navigate, dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  return {
    values,
    handleChange,
    formErrors,
    handleSignIn,
    account,
    isProcessing,
  };
};
